/**
 * Document de paramètres — annexe B, EF-PAR-001, EF-PAR-003.
 *
 * La structure `parameters` est partagée avec l'annexe A, à l'exception de
 * `display` : dans un document roadbook les filtres d'affichage sont portés par
 * le `display` racine, dans un fichier de paramètres par `parameters.display`.
 */

import { isCategoryKey, type CategoryKey } from '../core/categories';
import { DomainError, fail, ok, type Result } from '../core/errors';
import {
  formatLocalDateTime,
  formatTimeOfDay,
  nowRfc3339,
  parseLocalDateTime,
  parseTimeOfDay,
} from '../core/local-datetime';
import { defaultParameters, LIMITS, validateParameters, type DisplayParameters, type Parameters } from '../core/parameters';
import { JsonReader, schemaError } from './reader';

export const PARAMETERS_FORMAT = 'roadbookultra-parameters';
export const SCHEMA_VERSION = 1;
export const APP_NAME = 'RoadbookUltra';
export const APP_PLATFORM = 'web-pwa';

/** Plateformes acceptées à la lecture. `iOS` est une valeur historique valide. */
const ACCEPTED_PLATFORMS = ['web-pwa', 'iOS'] as const;

function readCategories(reader: JsonReader): readonly CategoryKey[] {
  const keys: CategoryKey[] = [];
  for (const item of reader.array()) {
    const value = item.string();
    if (!isCategoryKey(value)) throw schemaError(item.location, 'clé de catégorie inconnue');
    keys.push(value);
  }
  return keys;
}

function readLocalDateTime(reader: JsonReader) {
  const value = parseLocalDateTime(reader.string());
  if (value === null) throw schemaError(reader.location, 'une date-heure locale `YYYY-MM-DDTHH:mm:ss` est attendue');
  return value;
}

/** Lit le bloc `parameters`, avec ou sans `display` selon le document porteur. */
export function readParameters(reader: JsonReader, includeDisplay: boolean): Parameters {
  const defaults = defaultParameters();
  const search = reader.optional('search');
  const speed = reader.optional('speed');
  const schedule = reader.optional('schedule');
  const pauses = schedule.isAbsent ? schedule : schedule.optional('pauses');
  const sleep = schedule.isAbsent ? schedule : schedule.optional('sleep');
  const device = reader.optional('device');

  const parameters: Parameters = {
    search: {
      categories: search.isAbsent
        ? defaults.search.categories
        : search.optional('categories').withDefault(defaults.search.categories, readCategories),
      corridorMeters: search.isAbsent
        ? defaults.search.corridorMeters
        : search
            .optional('corridorMeters')
            .withDefault(defaults.search.corridorMeters, (value) => {
              value.integerInRange(LIMITS.corridorMeters.min, LIMITS.corridorMeters.max);
              return value.multipleOf(LIMITS.corridorMeters.step);
            }),
      skipStartMeters: search.isAbsent
        ? 0
        : search.optional('skipStartMeters').withDefault(0, (value) => value.integerInRange(0, Number.MAX_SAFE_INTEGER)),
      skipFinishMeters: search.isAbsent
        ? 0
        : search.optional('skipFinishMeters').withDefault(0, (value) => value.integerInRange(0, Number.MAX_SAFE_INTEGER)),
      budgetSeconds: search.isAbsent
        ? defaults.search.budgetSeconds
        : search
            .optional('budgetSeconds')
            .withDefault(defaults.search.budgetSeconds, (value) =>
              value.integerInRange(LIMITS.budgetSeconds.min, LIMITS.budgetSeconds.max),
            ),
    },
    speed: {
      referenceKmh: speed.isAbsent
        ? defaults.speed.referenceKmh
        : speed
            .optional('referenceKmh')
            .withDefault(defaults.speed.referenceKmh, (value) =>
              value.numberInRange(LIMITS.referenceKmh.min, LIMITS.referenceKmh.max),
            ),
      uphill5Kmh: speed.isAbsent
        ? defaults.speed.uphill5Kmh
        : speed
            .optional('uphill5Kmh')
            .withDefault(defaults.speed.uphill5Kmh, (value) =>
              value.numberInRange(LIMITS.uphill5Kmh.min, LIMITS.uphill5Kmh.max),
            ),
      maxKmh: speed.isAbsent
        ? defaults.speed.maxKmh
        : speed
            .optional('maxKmh')
            .withDefault(defaults.speed.maxKmh, (value) => value.numberInRange(LIMITS.maxKmh.min, LIMITS.maxKmh.max)),
      minKmh: speed.isAbsent
        ? defaults.speed.minKmh
        : speed
            .optional('minKmh')
            .withDefault(defaults.speed.minKmh, (value) => value.numberInRange(LIMITS.minKmh.min, LIMITS.minKmh.max)),
    },
    schedule: {
      startLocal: schedule.isAbsent
        ? defaults.schedule.startLocal
        : schedule.optional('startLocal').withDefault(defaults.schedule.startLocal, readLocalDateTime),
      pauses: {
        enabled: pauses.isAbsent ? false : pauses.optional('enabled').withDefault(false, (v) => v.boolean()),
        intervalSeconds: pauses.isAbsent
          ? defaults.schedule.pauses.intervalSeconds
          : pauses
              .optional('intervalSeconds')
              .withDefault(defaults.schedule.pauses.intervalSeconds, (value) =>
                value.integerInRange(LIMITS.pauseIntervalSeconds.min, LIMITS.pauseIntervalSeconds.max),
              ),
        durationSeconds: pauses.isAbsent
          ? defaults.schedule.pauses.durationSeconds
          : pauses
              .optional('durationSeconds')
              .withDefault(defaults.schedule.pauses.durationSeconds, (value) =>
                value.integerInRange(LIMITS.pauseDurationSeconds.min, LIMITS.pauseDurationSeconds.max),
              ),
      },
      sleep: {
        enabled: sleep.isAbsent ? false : sleep.optional('enabled').withDefault(false, (v) => v.boolean()),
        bedTimeSecondsOfDay: sleep.isAbsent
          ? defaults.schedule.sleep.bedTimeSecondsOfDay
          : sleep.optional('bedTimeLocal').withDefault(defaults.schedule.sleep.bedTimeSecondsOfDay, (value) => {
              const seconds = parseTimeOfDay(value.string());
              if (seconds === null) throw schemaError(value.location, 'une heure `HH:mm` est attendue');
              return seconds;
            }),
        durationSeconds: sleep.isAbsent
          ? defaults.schedule.sleep.durationSeconds
          : sleep
              .optional('durationSeconds')
              .withDefault(defaults.schedule.sleep.durationSeconds, (value) =>
                value.integerInRange(LIMITS.sleepDurationSeconds.min, LIMITS.sleepDurationSeconds.max),
              ),
        declinedByUser: sleep.isAbsent
          ? false
          : sleep.optional('declinedByUser').withDefault(false, (v) => v.boolean()),
      },
    },
    display: includeDisplay ? readDisplay(reader.optional('display')) : defaults.display,
    device: {
      keepScreenAwake: device.isAbsent
        ? false
        : device.optional('keepScreenAwake').withDefault(false, (v) => v.boolean()),
    },
  };

  return parameters;
}

export function readDisplay(reader: JsonReader): DisplayParameters {
  const defaults = defaultParameters().display;
  if (reader.isAbsent || reader.isNull) return defaults;
  return {
    hiddenCategories: reader.optional('hiddenCategories').withDefault(defaults.hiddenCategories, readCategories),
    pinnedOnly: reader.optional('pinnedOnly').withDefault(false, (v) => v.boolean()),
    excludedOnly: reader.optional('excludedOnly').withDefault(false, (v) => v.boolean()),
    groupingThresholdMeters: reader
      .optional('groupingThresholdMeters')
      .withDefault(defaults.groupingThresholdMeters, (value) => {
        value.integerInRange(LIMITS.groupingThresholdMeters.min, LIMITS.groupingThresholdMeters.max);
        return value.multipleOf(LIMITS.groupingThresholdMeters.step);
      }),
    // `visibleFields` est un champ hérité, ignoré conformément à EF-RBK-004.
  };
}

export function writeDisplay(display: DisplayParameters): Record<string, unknown> {
  return {
    hiddenCategories: [...display.hiddenCategories],
    pinnedOnly: display.pinnedOnly,
    excludedOnly: display.excludedOnly,
    groupingThresholdMeters: display.groupingThresholdMeters,
  };
}

/** Écrit le bloc `parameters`. `display` n'y figure que pour l'annexe B. */
export function writeParameters(parameters: Parameters, includeDisplay: boolean): Record<string, unknown> {
  const body: Record<string, unknown> = {
    search: {
      categories: [...parameters.search.categories],
      corridorMeters: parameters.search.corridorMeters,
      skipStartMeters: parameters.search.skipStartMeters,
      skipFinishMeters: parameters.search.skipFinishMeters,
      budgetSeconds: parameters.search.budgetSeconds,
    },
    speed: {
      referenceKmh: parameters.speed.referenceKmh,
      uphill5Kmh: parameters.speed.uphill5Kmh,
      maxKmh: parameters.speed.maxKmh,
      minKmh: parameters.speed.minKmh,
    },
    schedule: {
      startLocal: formatLocalDateTime(parameters.schedule.startLocal),
      pauses: {
        enabled: parameters.schedule.pauses.enabled,
        intervalSeconds: parameters.schedule.pauses.intervalSeconds,
        durationSeconds: parameters.schedule.pauses.durationSeconds,
      },
      sleep: {
        enabled: parameters.schedule.sleep.enabled,
        bedTimeLocal: formatTimeOfDay(parameters.schedule.sleep.bedTimeSecondsOfDay),
        durationSeconds: parameters.schedule.sleep.durationSeconds,
        declinedByUser: parameters.schedule.sleep.declinedByUser,
      },
    },
    device: { keepScreenAwake: parameters.device.keepScreenAwake },
  };
  if (includeDisplay) body['display'] = writeDisplay(parameters.display);
  return body;
}

export function writeGenerator(appVersion: string): Record<string, unknown> {
  return { name: APP_NAME, version: appVersion, platform: APP_PLATFORM };
}

export function readGenerator(reader: JsonReader): void {
  reader.required('name').string();
  reader.required('version').string();
  reader.required('platform').oneOf(ACCEPTED_PLATFORMS);
}

/** Sérialise un fichier de paramètres conforme à l'annexe B. */
export function serializeParametersFile(parameters: Parameters, appVersion: string): unknown {
  return {
    format: PARAMETERS_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    generator: writeGenerator(appVersion),
    exportedAt: nowRfc3339(),
    parameters: writeParameters(parameters, true),
  };
}

/**
 * Analyse un fichier de paramètres. Toute valeur hors contrainte rend le
 * fichier invalide dans sa totalité : aucune fusion partielle n'est autorisée.
 */
export function parseParametersFile(payload: unknown): Result<Parameters> {
  try {
    const root = JsonReader.root(payload);
    const format = root.required('format').string();
    if (format !== PARAMETERS_FORMAT) {
      return fail(
        new DomainError(
          'document/unknown-format',
          `Le fichier n'est pas un document de paramètres RoadbookUltra (format « ${format} »).`,
          format,
        ),
      );
    }

    const version = root.required('schemaVersion').integer();
    if (version !== SCHEMA_VERSION) {
      return fail(
        new DomainError(
          'document/unknown-schema-version',
          `Version de schéma inconnue (${version}). Une version plus récente de l'application peut être nécessaire.`,
          String(version),
        ),
      );
    }

    readGenerator(root.required('generator'));
    root.required('exportedAt').string();

    const parameters = readParameters(root.required('parameters'), true);

    // Les contraintes croisées de EF-CAL-001 invalident le fichier entier.
    const outcome = validateParameters(parameters, null);
    const firstIssue = outcome.issues[0];
    if (firstIssue !== undefined) {
      return fail(new DomainError('document/schema', `Champ « ${firstIssue.field} » : ${firstIssue.message}`, firstIssue.field));
    }

    return ok(parameters);
  } catch (error) {
    if (error instanceof DomainError) return fail(error);
    throw error;
  }
}
